'use client'

import Link, { type LinkProps } from 'next/link'
import { useMemo } from 'react'

import { useLocale } from '@src/i18n/LocaleProvider'
import { getDefaultLocale } from '@src/i18n/locale.util'
import { AuthRoutes } from '@src/utils/constants.util'
import { transformExternalBODiLink } from '@src/utils/externalBodiLink'
import { isExternalBODiLink } from '@src/utils/externalBodiLink'
import { useRouterCacheClear } from '@src/utils/flow-router.client.util'
import { log } from '@src/utils/log.util'

type LocalizedLinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> &
  LinkProps &
  React.RefAttributes<HTMLAnchorElement> & {
    children?: React.ReactNode
    targetCountry?: string
    targetLanguage?: string
    href: string
    useNextLink?: boolean
  }

const getTransformedHref = ({
  targetCountry,
  targetLanguage,
  lang,
  country,
  href,
}: {
  targetCountry?: string
  targetLanguage?: string
  lang: string
  country: string
  href: string
}) => {
  if (isExternalBODiLink(href)) {
    try {
      return transformExternalBODiLink(href)
    } catch (error) {
      log.error('Error transforming external BODi link', { error })
      return href
    }
  }

  if (Object.values(AuthRoutes).some((route) => href.startsWith(route))) {
    return href
  }

  if (href.startsWith('http')) {
    return href
  }

  const locale =
    targetCountry && targetLanguage ? `/${targetCountry}/${targetLanguage}` : `/${country}/${lang}`

  const defaultCountry = getDefaultLocale().countryCodeLowercase
  const isDefaultHomepage =
    href === '/' && (targetCountry ? targetCountry === defaultCountry : country === defaultCountry)

  if (isDefaultHomepage) {
    return href
  }

  return `${locale}${href}`
}

export const LocalizedLink = ({
  targetLanguage,
  targetCountry,
  href,
  children,
  useNextLink = true,
  ...props
}: LocalizedLinkProps) => {
  const { locale } = useLocale()
  const clearCache = useRouterCacheClear()
  const isSameOrigin = !href.startsWith('http')
  const transformedHref = useMemo(
    () =>
      getTransformedHref({
        targetCountry,
        targetLanguage,
        lang: locale.lang,
        country: locale.countryCodeLowercase,
        href,
      }),
    [locale, href, targetCountry, targetLanguage]
  )

  if (useNextLink && isSameOrigin && !clearCache) {
    return (
      <Link href={transformedHref} {...props}>
        {children}
      </Link>
    )
  }

  return (
    <a href={transformedHref} {...props}>
      {children}
    </a>
  )
}
